import { useMemo, useState } from 'react'

import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { zodValidator } from '@tanstack/zod-adapter'
import { Eye } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { JobUser } from '@/_gql/graphql'

import { TableJobUsers } from '@/services/job/components/table-job-users'
import { LANGUAGE_OPTIONS } from '@/services/user/constant'

import { AdminPage } from '@/components/layout/admin/page'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DebouncedInput } from '@/components/ui/debounce-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { formatDateOnly, getOneYearAgo } from '@/lib/utils/utils'

import { useFilters } from '@/hooks/use-filter'
import { dateFormat } from '@/lib/formatter'

const searchParamsSchema = z.object({
  language: z.string().nullish(),
  language_level: z.string().nullish(),
})

export const Route = createFileRoute('/admin-v2/job/applicant/vacancy/$jobId/')(
  {
    validateSearch: zodValidator(searchParamsSchema),
    loaderDeps: ({ search }) => search,
    component: RouteComponent,
  }
)

function RouteComponent() {
  const { jobId } = Route.useParams()
  return (
    <AdminPage>
      <AdminPage.Breadcrumb
        items={[
          ['Master Job Listing', '/admin-v2/job/vacancy'],
          ['List Job Listing', '/admin-v2/job/applicant/vacancy'],
          ['List Pelamar', '/admin-v2/job/applicant'],
        ]}
      />
      <AdminPage.Header title="List Pelamar" />
      <TableJobUsers jobId={jobId ? Number(jobId) : undefined} />
    </AdminPage>
  )
}

interface ApplicantListSectionProps {
  data?: JobUser[] | undefined
}

function ApplicantListSection({ data }: ApplicantListSectionProps) {
  const { filters, setFilters, resetFilters } = useFilters(
    '/admin-v2/job/applicant/vacancy/$jobId/'
  )
  const [openFilter, setOpenFilter] = useState(false)

  const [globalFilter, setGlobalFilter] = useState('')

  const columns = useMemo(getColumns, [])

  const { table } = useDataTable({
    data: data || [],
    columns,
    manualPagination: false,
    globalFilterFn: 'fuzzy',
    onGlobalFilterChange: setGlobalFilter,
    state: { globalFilter },
  })

  const handleFilterLanguageChange = (value: string) => {
    if (value === 'none') {
      resetFilters()
    } else {
      setFilters({
        ...filters,
        language: value,
      })
    }
  }

  const handleFilterLanguageLevelChange = (value: string) => {
    setFilters({
      ...filters,
      language_level: value,
    })
  }

  return (
    <Card>
      <CardContent className="overflow-auto p-4">
        <div className="mb-4 flex justify-between gap-4">
          <div className="flex w-full gap-6">
            <div className="w-1/2">
              <DebouncedInput
                className="lg:w-full"
                value={globalFilter ?? ''}
                onChange={(value) => setGlobalFilter(String(value))}
                placeholder="Cari .."
              />
            </div>
            <div className="flex w-1/2 gap-6">
              <div className="w-1/2">
                <Select
                  onValueChange={handleFilterLanguageChange}
                  value={filters.language ?? ''}
                >
                  <SelectTrigger className="data-[placeholder]:text-gray-300">
                    <SelectValue placeholder={'Filter Language'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="none" value="none">
                      None
                    </SelectItem>
                    {LANGUAGE_OPTIONS.map((item) => {
                      return (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
              {filters.language ? (
                <div className="w-1/2">
                  <Select
                    onValueChange={handleFilterLanguageLevelChange}
                    value={filters.language_level ?? ''}
                  >
                    <SelectTrigger className="data-[placeholder]:text-gray-300">
                      <SelectValue placeholder={'Filter Language Level'} />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.find(
                        (lang) => lang.value == filters.language
                      )?.level.map((item) => {
                        return (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <></>
              )}
            </div>
            {/* <div className="w-1/2">
                            <FilterCompany
                                defaultValue={filters.job_id}
                                setFilter={setFilters}
                            />
                        </div> */}
          </div>
          {/* <div>
                        <FilterTable
                            openFilter={openFilter}
                            setOpenFilter={setOpenFilter}
                            resetFilter={resetFilters}
                            setFilter={setFilters}
                            defaultValue={{
                                from: new Date(filters.start_apply_date ?? ''),
                                to: new Date(filters.end_apply_date ?? ''),
                            }}
                        />
                    </div> */}
        </div>
        {/* <DataTable table={table} /> */}
      </CardContent>
    </Card>
  )
}

const column = createColumnHelper<any>()

function getColumns() {
  return [
    column.accessor('id', {
      header: 'No',
    }),
    column.accessor('user.name', {
      header: 'Pelamar',
    }),
    column.accessor('job.title', {
      header: 'Job Listing',
    }),
    column.accessor('job.job_company.name', {
      header: 'Perusahaan',
    }),

    column.accessor('job_user_selection_phase[0].created_at', {
      header: 'Tanggal Apply',
      cell: (cell) => {
        const { job_user_selection_phase } = cell.row.original
        const apply_date = job_user_selection_phase[0]?.created_at
        const date = apply_date ? dateFormat(apply_date, 'dd LLL yyyy') : ''

        return <div>{date}</div>
      },
    }),
    column.accessor('test_result', {
      header: 'Hasil Rekomendasi',
      cell: (cell) => {
        const data = cell.row.original
        const last_job_user_selection_phase =
          data?.last_job_user_selection_phase
        const result =
          last_job_user_selection_phase?.ai_recommender?.is_recommended_by_ai
        const ai_cv_recommender =
          last_job_user_selection_phase?.job_selection_phase
            ?.master_job_selection_phase?.ai_cv_recommender

        if (
          last_job_user_selection_phase?.ai_recommender &&
          result &&
          ai_cv_recommender
        ) {
          return <div className="text-[#34C759]">Direkomendasikan</div>
        } else if (
          last_job_user_selection_phase?.ai_recommender &&
          !result &&
          ai_cv_recommender
        ) {
          return <div className="text-[#FF3B30]">Gagal</div>
        } else {
          return <div>Tidak memakai AI untuk rekomendasi</div>
        }
      },
    }),
    column.display({
      id: 'actions',
      header: 'Aksi',
      cell: ({ cell }) => {
        const { id, title } = cell.row.original
        return (
          <div className="flex gap-1.5">
            {/* <Button
              size="icon"
              className="size-8 text-primary-yes"
              variant="ghost"
            >
              <Check />
            </Button>
            <Button
              size="icon"
              className="size-8 text-primary-yes"
              variant="ghost"
            >
              <X />
            </Button> */}
            <Link
              from={Route.fullPath}
              to="/admin-v2/job/applicant/$applicantId"
              params={{ applicantId: String(id) }}
            >
              <Button
                size="icon"
                className="size-8 text-primary-yes"
                variant="ghost"
              >
                <Eye />
              </Button>
            </Link>
          </div>
        )
      },
    }),
  ]
}
